import { Capacitor } from "@capacitor/core";

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export function isNativeIOS(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

export function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

export function isSupportedNativeParticipantPlatform(): boolean {
  return isNativeIOS() || isNativeAndroid();
}

type NavigatorWithUserAgentData = Navigator & {
  brave?: {
    isBrave?: () => Promise<boolean>;
  };
  userAgentData?: {
    brands?: Array<{
      brand?: string;
      version?: string;
    }>;
    mobile?: boolean;
  };
};

type NavigatorWithBluetoothAvailability = Navigator & {
  bluetooth?: {
    getAvailability?: () => Promise<boolean>;
    addEventListener?: (
      type: string,
      listener: EventListenerOrEventListenerObject
    ) => void;
    removeEventListener?: (
      type: string,
      listener: EventListenerOrEventListenerObject
    ) => void;
  };
};

type DocumentWithPermissionsPolicy = Document & {
  permissionsPolicy?: {
    allowsFeature?: (feature: string) => boolean;
  };
  featurePolicy?: {
    allowsFeature?: (feature: string) => boolean;
  };
};

export type ParticipantRuntimeKind =
  | "native_participant"
  | "supported_participant_web"
  | "admin_only_web"
  | "unsupported_participant_web";

export type ParticipantRuntimePolicyReason =
  | "native_ios"
  | "native_android"
  | "desktop_chrome"
  | "unsupported_browser"
  | "unsupported_mobile_web"
  | "unsupported_native_platform"
  | "insecure_context"
  | "blocked_by_permissions_policy"
  | "unknown";

export type ParticipantRuntimePolicy = {
  kind: ParticipantRuntimeKind;
  reason: ParticipantRuntimePolicyReason;
  browserName: string | null;
  isMobileWeb: boolean;
  participantRoutesAllowed: boolean;
  participantLoginAllowed: boolean;
  adminToolsAllowed: boolean;
  message: string | null;
};

export type BluetoothTransportProbe = {
  supported: boolean;
  available: boolean | null;
  availabilityKnown: boolean;
  reason:
    | "native_ios"
    | "native_android"
    | "insecure_context"
    | "blocked_by_permissions_policy"
    | "missing_web_bluetooth"
    | "bluetooth_unavailable"
    | "available"
    | "unknown";
};

export function isBluetoothTransportGateBlocked(
  probe: Pick<BluetoothTransportProbe, "reason"> | null | undefined
): boolean {
  const reason = probe?.reason;
  return (
    reason === "insecure_context" ||
    reason === "blocked_by_permissions_policy" ||
    reason === "missing_web_bluetooth"
  );
}

export function getBluetoothTransportGateMessage(
  probe: Pick<BluetoothTransportProbe, "reason"> | null | undefined
): string {
  switch (probe?.reason) {
    case "insecure_context":
      return "Participant sign-in requires HTTPS (or 127.0.0.1) before Bluetooth can be used.";
    case "blocked_by_permissions_policy":
      return "This page is blocking Bluetooth via Permissions-Policy, so participant sign-in is disabled here.";
    case "missing_web_bluetooth":
      return "This browser does not expose Web Bluetooth, so participant sign-in is disabled here.";
    default:
      return "Bluetooth support could not be confirmed for participant sign-in.";
  }
}

function isBluetoothAllowedByPermissionsPolicy(): boolean | null {
  if (typeof document === "undefined") return null;

  const docWithPolicy = document as DocumentWithPermissionsPolicy;
  const policy = docWithPolicy.permissionsPolicy ?? docWithPolicy.featurePolicy;
  if (!policy || typeof policy.allowsFeature !== "function") {
    return null;
  }

  try {
    return policy.allowsFeature("bluetooth");
  } catch {
    return null;
  }
}

function getNavigatorUserAgent(): string {
  if (typeof navigator === "undefined") return "";
  return navigator.userAgent || "";
}

function getNavigatorBrands(): string[] {
  if (typeof navigator === "undefined") return [];

  const brands = (navigator as NavigatorWithUserAgentData).userAgentData?.brands;
  if (!Array.isArray(brands)) return [];

  return brands
    .map((entry) =>
      typeof entry?.brand === "string" ? entry.brand.trim().toLowerCase() : ""
    )
    .filter(Boolean);
}

function hasNavigatorBrand(pattern: RegExp): boolean {
  return getNavigatorBrands().some((brand) => pattern.test(brand));
}

function detectMobileWeb(): boolean {
  if (typeof navigator === "undefined") return false;

  const userAgentData = (navigator as NavigatorWithUserAgentData).userAgentData;
  if (typeof userAgentData?.mobile === "boolean") {
    return userAgentData.mobile;
  }

  const userAgent = getNavigatorUserAgent();
  if (/android|iphone|ipod|mobile/i.test(userAgent)) {
    return true;
  }
  if (/ipad/i.test(userAgent)) {
    return true;
  }
  if (/macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1) {
    return true;
  }

  return false;
}

function detectBrowserName(): string | null {
  const userAgent = getNavigatorUserAgent();

  if (hasNavigatorBrand(/edge/i) || /edg\//i.test(userAgent)) {
    return "Edge";
  }
  if (hasNavigatorBrand(/opera/i) || /opr\//i.test(userAgent)) {
    return "Opera";
  }
  if (hasNavigatorBrand(/firefox/i) || /firefox\//i.test(userAgent)) {
    return "Firefox";
  }
  if (
    hasNavigatorBrand(/safari/i) ||
    (/safari/i.test(userAgent) &&
      !/chrome|crios|chromium|edg\/|opr\//i.test(userAgent))
  ) {
    return "Safari";
  }
  if (hasNavigatorBrand(/chrome|chromium/i) || /chrome\//i.test(userAgent)) {
    return "Chrome";
  }

  return null;
}

function isDesktopChromeBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  if (detectMobileWeb()) return false;

  const userAgent = getNavigatorUserAgent();
  const brands = getNavigatorBrands();
  const hasChromiumBrand = brands.some((brand) => /chrome|chromium/i.test(brand));
  const hasDisallowedBrand = brands.some((brand) =>
    /edge|opera|vivaldi|duckduckgo|yandex|samsung|firefox|safari|brave/i.test(
      brand
    )
  );
  const hasChromeToken = /chrome\//i.test(userAgent);
  const hasDisallowedToken =
    /edg\/|opr\/|vivaldi|samsungbrowser|yabrowser|duckduckgo|firefox\//i.test(
      userAgent
    );
  const isBrave =
    typeof (navigator as NavigatorWithUserAgentData).brave === "object";

  return (
    (hasChromiumBrand || hasChromeToken) &&
    !hasDisallowedBrand &&
    !hasDisallowedToken &&
    !isBrave
  );
}

export function resolveParticipantRuntimePolicy(): ParticipantRuntimePolicy {
  if (typeof window === "undefined") {
    return {
      kind: "unsupported_participant_web",
      reason: "unknown",
      browserName: null,
      isMobileWeb: false,
      participantRoutesAllowed: false,
      participantLoginAllowed: false,
      adminToolsAllowed: false,
      message: "Checking participant browser support...",
    };
  }

  if (isNativeIOS() || isNativeAndroid()) {
    return {
      kind: "native_participant",
      reason: isNativeIOS() ? "native_ios" : "native_android",
      browserName: null,
      isMobileWeb: false,
      participantRoutesAllowed: true,
      participantLoginAllowed: true,
      adminToolsAllowed: false,
      message: null,
    };
  }

  if (isNativePlatform()) {
    return {
      kind: "unsupported_participant_web",
      reason: "unsupported_native_platform",
      browserName: null,
      isMobileWeb: false,
      participantRoutesAllowed: false,
      participantLoginAllowed: false,
      adminToolsAllowed: false,
      message:
        "Participant sessions require the mobile app or Chrome on desktop.",
    };
  }

  const browserName = detectBrowserName();
  const isMobileWeb = detectMobileWeb();

  if (isMobileWeb) {
    return {
      kind: "unsupported_participant_web",
      reason: "unsupported_mobile_web",
      browserName,
      isMobileWeb: true,
      participantRoutesAllowed: false,
      participantLoginAllowed: false,
      adminToolsAllowed: false,
      message:
        "Participant sessions are not supported in mobile browsers. Use the mobile app or Chrome on desktop.",
    };
  }

  if (!isDesktopChromeBrowser()) {
    return {
      kind: "admin_only_web",
      reason: "unsupported_browser",
      browserName,
      isMobileWeb: false,
      participantRoutesAllowed: false,
      participantLoginAllowed: false,
      adminToolsAllowed: true,
      message:
        "Participant sessions require Chrome on desktop or the mobile app. Admin tools remain available in this browser.",
    };
  }

  if (!window.isSecureContext) {
    return {
      kind: "supported_participant_web",
      reason: "insecure_context",
      browserName: "Chrome",
      isMobileWeb: false,
      participantRoutesAllowed: false,
      participantLoginAllowed: false,
      adminToolsAllowed: true,
      message:
        "Participant sessions require HTTPS (or 127.0.0.1) before Bluetooth can be used.",
    };
  }

  if (isBluetoothAllowedByPermissionsPolicy() === false) {
    return {
      kind: "supported_participant_web",
      reason: "blocked_by_permissions_policy",
      browserName: "Chrome",
      isMobileWeb: false,
      participantRoutesAllowed: false,
      participantLoginAllowed: false,
      adminToolsAllowed: true,
      message:
        "This page is blocking Bluetooth via Permissions-Policy, so participant sign-in is disabled here.",
    };
  }

  return {
    kind: "supported_participant_web",
    reason: "desktop_chrome",
    browserName: "Chrome",
    isMobileWeb: false,
    participantRoutesAllowed: true,
    participantLoginAllowed: true,
    adminToolsAllowed: true,
    message: null,
  };
}

export async function probeBluetoothTransportAvailability(): Promise<BluetoothTransportProbe> {
  if (typeof window === "undefined") {
    return {
      supported: false,
      available: null,
      availabilityKnown: false,
      reason: "unknown",
    };
  }

  if (isNativeIOS() || isNativeAndroid()) {
    return {
      supported: true,
      available: true,
      availabilityKnown: false,
      reason: isNativeIOS() ? "native_ios" : "native_android",
    };
  }

  if (!window.isSecureContext) {
    return {
      supported: false,
      available: null,
      availabilityKnown: false,
      reason: "insecure_context",
    };
  }

  if (isBluetoothAllowedByPermissionsPolicy() === false) {
    return {
      supported: false,
      available: false,
      availabilityKnown: true,
      reason: "blocked_by_permissions_policy",
    };
  }

  if (!("bluetooth" in navigator)) {
    return {
      supported: false,
      available: null,
      availabilityKnown: false,
      reason: "missing_web_bluetooth",
    };
  }

  const bluetooth = (navigator as NavigatorWithBluetoothAvailability).bluetooth;
  if (!bluetooth || typeof bluetooth.getAvailability !== "function") {
    return {
      supported: true,
      available: null,
      availabilityKnown: false,
      reason: "unknown",
    };
  }

  try {
    const available = await bluetooth.getAvailability();
    if (available) {
      return {
        supported: true,
        available: true,
        availabilityKnown: true,
        reason: "available",
      };
    }
    return {
      supported: false,
      available: false,
      availabilityKnown: true,
      reason: "bluetooth_unavailable",
    };
  } catch {
    return {
      supported: true,
      available: null,
      availabilityKnown: false,
      reason: "unknown",
    };
  }
}

export function subscribeBluetoothTransportAvailability(
  onChange: () => void
): () => void {
  if (typeof window === "undefined") return () => {};

  const bluetooth = (navigator as NavigatorWithBluetoothAvailability).bluetooth;
  const handleChange = () => onChange();

  if (bluetooth && typeof bluetooth.addEventListener === "function") {
    try {
      bluetooth.addEventListener("availabilitychanged", handleChange);
      return () => {
        try {
          bluetooth.removeEventListener?.("availabilitychanged", handleChange);
        } catch {
          // Ignore cleanup failures.
        }
      };
    } catch {
      // Fall through to generic listeners below.
    }
  }

  window.addEventListener("focus", handleChange);
  document.addEventListener("visibilitychange", handleChange);
  return () => {
    window.removeEventListener("focus", handleChange);
    document.removeEventListener("visibilitychange", handleChange);
  };
}

export function isBluetoothTransportAvailable(): boolean {
  if (typeof window === "undefined") return false;
  if (isNativeIOS() || isNativeAndroid()) return true;
  if (!window.isSecureContext) return false;
  if (isBluetoothAllowedByPermissionsPolicy() === false) return false;
  return "bluetooth" in navigator;
}
